import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Book, MessageSquare, Send, CheckCircle, Cross, GraduationCap, Lightbulb, HelpingHand as PrayingHands } from 'lucide-react';
import SubmissionPopup from '../components/SubmissionPopup';
import MouseFollowEffect from '../components/MouseFollowEffect';
import QuestionNavigator from '../components/QuestionNavigator';

interface FormData {
  name: string;
  email: string;
  company: string;
  answers: Record<string, string | string[]>;
}

export default function WesternChristianAcademy() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    answers: {}
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showMeetingPopup, setShowMeetingPopup] = useState(false);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const formContainerRef = useRef<HTMLDivElement>(null);

  const questions = [
    {
      id: 'content_goals',
      category: 'Content Goals',
      question: 'Dr. Kneeland, what are your primary content goals for Western Christian Academy?',
      type: 'select',
      options: [
        'Increasing student enrollment and applications',
        'Enhancing the academy\'s Christian reputation',
        'Showcasing faculty expertise and faith integration',
        'Improving parent and community engagement',
        'Highlighting student success stories and testimonials',
        'All of the above'
      ]
    },
    {
      id: 'target_audience',
      category: 'Target Audience',
      question: 'Who are the most important audiences you\'re trying to reach with your content?',
      type: 'multiselect',
      options: [
        'Christian families seeking faith-based education',
        'Prospective students',
        'Current students and parents',
        'Alumni and supporters',
        'Church communities',
        'Christian education peers',
        'Donors and financial supporters',
        'Local community'
      ]
    },
    {
      id: 'faith_integration',
      category: 'Faith Integration',
      question: 'How important is it that your content explicitly reflects Christian values and teachings?',
      type: 'select',
      options: [
        'Essential - faith should be central in all content',
        'Very important - most content should reflect our faith',
        'Important - key content should include faith elements',
        'Somewhat important - faith should be present but subtle',
        'Varies by content type and audience'
      ]
    },
    {
      id: 'content_challenges',
      category: 'Content Challenges',
      question: 'What are the biggest challenges Western Christian Academy faces with content creation?',
      type: 'multiselect',
      options: [
        'Limited internal resources for content creation',
        'Balancing academic excellence with faith messaging',
        'Consistently producing fresh, engaging content',
        'Standing out among other Christian schools',
        'Effectively communicating our unique approach',
        'Reaching new audiences beyond our current community',
        'Measuring content effectiveness',
        'Other'
      ]
    },
    {
      id: 'content_types',
      category: 'Content Types',
      question: 'Which content types would be most valuable for Western Christian Academy?',
      type: 'multiselect',
      options: [
        'Faith-integrated academic articles',
        'Student and alumni testimonials',
        'Faculty spotlights and thought leadership',
        'Virtual campus tours and event highlights',
        'Biblical worldview educational resources',
        'Parent resources and guidance',
        'Ministry and outreach stories',
        'Interactive digital experiences'
      ]
    },
    {
      id: 'content_frequency',
      category: 'Content Frequency',
      question: 'How frequently would you ideally like to publish new content?',
      type: 'select',
      options: [
        'Multiple times per week',
        'Weekly',
        'Bi-weekly',
        'Monthly',
        'Quarterly',
        'Varies by channel and content type'
      ]
    },
    {
      id: 'success_metrics',
      category: 'Success Metrics',
      question: 'How will you measure the success of our content collaboration?',
      type: 'multiselect',
      options: [
        'Increased enrollment inquiries',
        'Higher website traffic and engagement',
        'Growth in social media following and engagement',
        'Positive feedback from current families',
        'Increased donations and financial support',
        'Greater awareness in the Christian community',
        'Improved retention of current students',
        'Other'
      ]
    },
    {
      id: 'testimonials',
      category: 'Testimonials',
      question: 'Do you currently collect testimonials from students, parents, and alumni?',
      type: 'select',
      options: [
        'Yes, we have a robust testimonial collection process',
        'Yes, but our collection is inconsistent',
        'We collect some, but need a better system',
        'We rarely collect testimonials',
        'No, but we recognize their importance'
      ]
    },
    {
      id: 'content_approval',
      category: 'Content Approval',
      question: 'What is your preferred content approval process to ensure alignment with your Christian values?',
      type: 'select',
      options: [
        'Single approver (President/Principal)',
        'Small committee of leadership',
        'Department heads for relevant content',
        'Collaborative review with faculty input',
        'Flexible process depending on content type'
      ]
    },
    {
      id: 'budget',
      category: 'Investment',
      question: 'What monthly budget has Western Christian Academy allocated for content creation?',
      type: 'select',
      options: [
        '$10,000 - $15,000 per month',
        '$15,000 - $20,000 per month',
        '$20,000 - $25,000 per month',
        '$25,000+ per month'
      ]
    }
  ];

  // Explicitly prevent scrolling on initial load
  React.useEffect(() => {
    // Force scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  // Only scroll to form container when changing steps, not on initial load
  React.useEffect(() => {
    if (currentStep > 0 && formContainerRef.current) {
      formContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentStep]);

  const handleInputChange = (questionId: string, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: value
      }
    }));
  };

  const handleBasicInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNoteChange = (questionId: string, note: string) => {
    setNotes(prev => ({
      ...prev,
      [questionId]: note
    }));
  };

  const handleNext = () => {
    if (currentStep < questions.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleMultiSelectChange = (questionId: string, option: string) => {
    setFormData(prev => {
      const currentSelections = Array.isArray(prev.answers[questionId]) 
        ? prev.answers[questionId] as string[] 
        : [];
      
      const updatedSelections = currentSelections.includes(option)
        ? currentSelections.filter(item => item !== option)
        : [...currentSelections, option];
      
      return {
        ...prev,
        answers: {
          ...prev.answers,
          [questionId]: updatedSelections
        }
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://hook.eu2.make.com/5axqhl62q3qrvdrqz3jlvxqmf9ynbl2f', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'western_christian_academy',
          timestamp: new Date().toISOString(),
          name: formData.name,
          email: formData.email,
          company: formData.company,
          answers: formData.answers,
          notes: notes
        }),
      });
      
      if (response.ok) {
        setShowMeetingPopup(true);
      } else {
        console.error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleMeeting = () => {
    window.location.href = 'https://cal.com/dsmarketing.meeting.with.pascoal/western-christian-academy-ds-marketing-1st-content-creation-talk';
  };

  const handleDeclineMeeting = () => {
    setShowMeetingPopup(false);
    setShowPopup(true);
    // Reset form after successful submission
    setFormData({
      name: '',
      email: '',
      company: '',
      answers: {}
    });
    setNotes({});
    setCurrentStep(0);
  };

  return (
    <div className="page-container pt-20 min-h-screen">
      <div className="content-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="relative">
              <Book className="w-16 h-16 text-golden animate-pulse" />
              <Cross className="w-8 h-8 text-blue-400 absolute -top-2 -right-2 animate-pulse" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Western <span className="text-golden">Christian</span> Academy
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Personalized for Dr. Clinton Kneeland, President
          </p>
          <div className="mt-6 max-w-3xl mx-auto bg-black/30 p-6 rounded-xl border border-gray-800">
            <p className="text-gray-300">
              We're honored to connect with Western Christian Academy. As fellow believers, our team of Christians understands the unique mission of faith-based education and the importance of integrating Biblical principles in all content. We'd love to learn more about your content creation needs to help elevate your academy's message.
            </p>
          </div>
        </motion.div>

        <div className="bg-black/20 border border-gray-800 rounded-2xl p-8 mb-12 max-w-4xl mx-auto service-card" ref={formContainerRef}>
          <QuestionNavigator isActive={currentStep === 0}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="text-2xl font-bold mb-6 text-golden">Let's Get Started</h2>
              <p className="text-gray-300 mb-8">
                Please provide some basic information before we dive into the specific questions about Western Christian Academy's content creation needs.
              </p>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Your Name <span className="text-golden">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleBasicInfoChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                    placeholder="Dr. Clinton Kneeland"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address <span className="text-golden">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleBasicInfoChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                    placeholder="ckneeland@westernchristianacademy.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                    Institution <span className="text-golden">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleBasicInfoChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                    placeholder="Western Christian Academy"
                  />
                </div>
                
                <div className="pt-4">
                  <button
                    onClick={handleNext}
                    disabled={!formData.name || !formData.email || !formData.company}
                    className="w-full px-6 py-4 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue to Questions
                  </button>
                </div>
              </div>
            </motion.div>
          </QuestionNavigator>

          {questions.map((question, index) => {
            const stepNumber = index + 1;
            return (
              <QuestionNavigator 
                key={question.id} 
                isActive={currentStep === stepNumber}
              >
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">
                      Question {stepNumber} of {questions.length}
                    </span>
                    <span className="text-sm font-medium text-golden">
                      {question.category}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold">
                    {question.question}
                  </h3>
                  
                  {question.type === 'select' && (
                    <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2 pb-2">
                      {question.options.map((option) => (
                        <div 
                          key={option}
                          onClick={() => handleInputChange(question.id, option)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                            formData.answers[question.id] === option
                              ? 'border-golden bg-golden/10'
                              : 'border-gray-800 bg-black/30 hover:border-gray-600'
                          }`}
                        >
                          <div className="flex items-center">
                            <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                              formData.answers[question.id] === option
                                ? 'border-golden'
                                : 'border-gray-600'
                            }`}>
                              {formData.answers[question.id] === option && (
                                <div className="w-3 h-3 rounded-full bg-golden" />
                              )}
                            </div>
                            <span className="text-gray-300">{option}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {question.type === 'multiselect' && (
                    <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2 pb-2">
                      {question.options.map((option) => {
                        const isSelected = Array.isArray(formData.answers[question.id]) && 
                          (formData.answers[question.id] as string[]).includes(option);
                        
                        return (
                          <div 
                            key={option}
                            onClick={() => handleMultiSelectChange(question.id, option)}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                              isSelected
                                ? 'border-golden bg-golden/10'
                                : 'border-gray-800 bg-black/30 hover:border-gray-600'
                            }`}
                          >
                            <div className="flex items-center">
                              <div className={`w-5 h-5 rounded-md border-2 mr-3 flex items-center justify-center ${
                                isSelected
                                  ? 'border-golden'
                                  : 'border-gray-600'
                              }`}>
                                {isSelected && (
                                  <CheckCircle className="w-3 h-3 text-golden" />
                                )}
                              </div>
                              <span className="text-gray-300">{option}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Notes (for internal use only)
                    </label>
                    <div className="flex items-start space-x-2">
                      <MessageSquare className="w-5 h-5 text-gray-400 mt-3" />
                      <textarea
                        value={notes[question.id] || ''}
                        onChange={(e) => handleNoteChange(question.id, e.target.value)}
                        placeholder="Add notes about this response..."
                        className="flex-1 px-4 py-3 rounded-lg bg-black/30 border border-gray-700 focus:outline-none focus:border-gray-500 text-gray-400 text-sm min-h-[80px]"
                      />
                    </div>
                  </div>
                  
                  <div className="pt-4 flex justify-between">
                    <button
                      onClick={handlePrevious}
                      className="px-6 py-3 border-2 border-gray-700 text-gray-300 rounded-full font-medium hover:border-golden hover:text-golden transition-all duration-300"
                    >
                      Previous
                    </button>
                    
                    {stepNumber < questions.length ? (
                      <button
                        onClick={handleNext}
                        disabled={!formData.answers[question.id] || 
                          (Array.isArray(formData.answers[question.id]) && 
                          (formData.answers[question.id] as string[]).length === 0)}
                        className="px-6 py-3 bg-golden text-white rounded-full font-medium hover:bg-golden-600 transition-all duration-300 disabled:opacity-70"
                      >
                        Next Question
                      </button>
                    ) : (
                      <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="px-6 py-3 bg-golden text-white rounded-full font-medium hover:bg-golden-600 transition-all duration-300 disabled:opacity-70"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Responses'}
                      </button>
                    )}
                  </div>
                </div>
              </QuestionNavigator>
            );
          })}

          <QuestionNavigator isActive={currentStep > questions.length}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="text-center py-8"
            >
              <CheckCircle className="w-16 h-16 text-golden mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-4">Thank You!</h2>
              <p className="text-gray-300 mb-6">
                Your responses have been submitted successfully. We'll be in touch soon with a tailored content strategy for Western Christian Academy.
              </p>
            </motion.div>
          </QuestionNavigator>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-gradient-to-r from-blue-900/30 to-golden/20 rounded-2xl p-8 border border-golden/20">
            <h2 className="text-2xl font-bold mb-4">Why Partner with DS Marketing for Western Christian Academy</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-golden mb-2">Faith-Aligned Team</h3>
                <p className="text-gray-300">
                  Our team is composed entirely of Christians who understand and share your values. We approach content creation with Biblical principles at the forefront.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-golden mb-2">Christian Education Expertise</h3>
                <p className="text-gray-300">
                  We've helped numerous faith-based educational institutions elevate their digital presence with content that resonates with Christian families and supporters.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-golden mb-2">Guaranteed Results</h3>
                <p className="text-gray-300">
                  We don't just create content—we deliver measurable outcomes that advance your mission. Our approach is designed to increase enrollment, engagement, and support for your academy.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-golden mb-2">Mission-Focused Approach</h3>
                <p className="text-gray-300">
                  We understand that your content needs to balance academic excellence with spiritual formation. Our strategies are tailored to communicate both effectively.
                </p>
              </div>
            </div>
            <div className="mt-6 text-center">
              <div className="flex items-center justify-center mb-4">
                <PrayingHands className="w-6 h-6 text-golden mr-2" />
                <p className="text-gray-300 italic">
                  "And whatever you do, whether in word or deed, do it all in the name of the Lord Jesus, giving thanks to God the Father through him." — Colossians 3:17
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-black/20 border border-gray-800 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-6 text-golden">Questions Asked in This Form</h2>
            <ol className="list-decimal pl-6 space-y-3">
              {questions.map((question) => (
                <li key={question.id} className="text-gray-300">
                  {question.question}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
      
      {/* Meeting Popup */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: showMeetingPopup ? 1 : 0 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 ${
          showMeetingPopup ? 'visible' : 'invisible'
        }`}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: showMeetingPopup ? 1 : 0.9, opacity: showMeetingPopup ? 1 : 0 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-black/90 rounded-2xl p-8 max-w-md w-full border border-golden/30"
        >
          <div className="text-center">
            <GraduationCap className="w-16 h-16 text-golden mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-4">
              Elevate Western Christian Academy's Content Strategy
            </h2>
            <p className="text-lg text-gray-300 mb-6">
              Thank you for sharing your insights, Dr. Kneeland. We're excited about the opportunity to help Western Christian Academy share its mission more effectively.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleScheduleMeeting}
                className="px-6 py-3 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300"
              >
                Schedule Meeting For Tuesday
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
      
      <SubmissionPopup 
        isOpen={showPopup} 
        onClose={() => setShowPopup(false)} 
        message="Thank you for your time Dr. Kneeland! You will be redirected to schedule your meeting shortly."
        redirectUrl="https://cal.com/dsmarketing.meeting.with.pascoal/western-christian-academy-ds-marketing-1st-content-creation-talk"
      />
    </div>
  );
}